import { createAction, createSlice } from '@reduxjs/toolkit'
import { RootState } from 'common/redux'
import {
	FeaturesChangedEvent,
	isInteractionSchemeAddress,
	isRoomAddress,
} from 'common/types'
import {
	SessionMode,
	sessionModeOfInteractionScheme,
	sessionModeOfRoom,
} from './model/session-mode'

export type SessionModeState = {
	/**
	 * The scheme the mode below was read from. A blended training rebuilds the
	 * interaction scheme when it switches between an online and an in-person
	 * chunk, so a new id means the answer has to be read again rather than
	 * carried over.
	 */
	interactionSchemeId?: string
	mode?: SessionMode
	/**
	 * The scheme reported its features and the list was empty. A remote scheme
	 * always lists its microphone and camera, so only a stationary scheme
	 * without an agenda (no `ScriptFeatureType`) gets here - see
	 * `selectSessionMode`.
	 */
	schemeListedNothing?: boolean
}

const INITIAL_STATE: SessionModeState = {}

const featuresChanged = createAction<FeaturesChangedEvent>(
	'SSE_FEATURES_CHANGED'
)

export const sessionModeSlice = createSlice({
	name: 'sessionMode',
	initialState: INITIAL_STATE,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(featuresChanged, (state, { payload }): void => {
			const { context, enabled } = payload
			const schemeLevel = isInteractionSchemeAddress(context)

			if (!schemeLevel && !isRoomAddress(context)) {
				return
			}

			const { interactionSchemeId } = context
			if (state.interactionSchemeId !== interactionSchemeId) {
				state.interactionSchemeId = interactionSchemeId
				state.mode = undefined
				state.schemeListedNothing = undefined
			}

			/**
			 * The scheme's own list is the one that reaches every room, which is
			 * what makes the lobby readable: a stationary scheme starts in its
			 * `BREAK` phase and puts a user who joins early into a lobby whose
			 * own feature list is empty.
			 *
			 * It wins when it has an answer, and leaves the previous one alone
			 * when it does not, exactly like a room that cannot tell.
			 */
			if (schemeLevel) {
				const schemeVerdict = sessionModeOfInteractionScheme(enabled)
				if (schemeVerdict) {
					state.mode = schemeVerdict
				}
				state.schemeListedNothing = enabled.length === 0
				return
			}

			/**
			 * Rooms that cannot tell leave the answer alone rather than unsetting
			 * it, so walking into a break does not make the app forget what kind
			 * of session it is in.
			 */
			const verdict = sessionModeOfRoom(enabled)
			if (verdict) {
				state.mode = verdict
			}
		})
	},
})

/**
 * The mode the features gave, or - when the scheme listed no features at all -
 * the one the training says its current chunk is. A stationary scheme without
 * an agenda enables nothing on the scheme and nothing on its lobby, and
 * waiting for evidence there meant a spinner forever, with the trainer's
 * Resume button never mounted. The empty list alone is not enough: it also
 * fits a scheme still registering its features.
 */
export const selectSessionMode = (state: RootState): SessionMode | undefined =>
	state.sessionMode.mode ??
	(state.sessionMode.schemeListedNothing &&
	state.sessionInfo.training?.currentChunkSessionType === 'IN_PERSON'
		? 'IN_PERSON'
		: undefined)

export const reducer = sessionModeSlice.reducer
